// MyPlaces — iter109 layout.
//
// Changes vs iter107:
//   • Extracted the inline PlaceActionsRow into a shared
//     CompactActionsRow component (used by MyFavorites too).
//
// Layout (unchanged):
//   • "Guardar un Lugar" CTA at top, above the list.
//   • Per-card compact 3-button row (Ride / Directions / phone-if-WA).
//
// Sources:
//   • Logged-in → GET /api/custom-locations.
//   • Guest     → guestStore.getCustomLocations().
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, MapPin, Loader2, Gem } from "lucide-react";
import { api, getMe } from "../lib/api";
import { BottomNav } from "../components/BottomNav";
import CompactActionsRow from "../components/CompactActionsRow";
import { useI18n } from "../lib/i18n";
import { EmptyState, Thumb } from "./MyFavorites";
import * as guestStore from "../lib/guestStorage";

export default function MyPlaces() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const me = await getMe();
      if (cancelled) return;
      if (!me) {
        setItems(guestStore.getCustomLocations());
        setLoading(false);
        return;
      }
      try {
        const { data } = await api.get("/custom-locations");
        if (cancelled) return;
        setItems(data || []);
      } catch { /* ignore */ }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <main data-testid="my-places-page" className="min-h-screen pb-28" style={{ background: "#0B0B0B" }}>
      <header className="px-5 pt-6 pb-3 flex items-center gap-3">
        <button onClick={() => navigate(-1)} aria-label="Back" data-testid="my-places-back"
          className="grid place-items-center"
          style={{ minHeight: 48, minWidth: 48, marginLeft: -8 }}>
          <ArrowLeft className="w-5 h-5 text-foreground/80" strokeWidth={1.5} />
        </button>
        <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, color: "#F5F1E8" }}>
          {t("page_my_places_title")}
        </h1>
      </header>

      {/* iter107 — Save CTA at top of page, above the list */}
      {!loading && (
        <div className="px-5 mt-2">
          <Link
            to="/my-places/custom/new"
            data-testid="my-places-save-cta"
            className="w-full inline-flex items-center justify-center"
            style={{
              background: "#E8B84B", color: "#0B0B0B",
              fontFamily: "'DM Sans', sans-serif", fontSize: 16, fontWeight: 700,
              padding: "12px 24px", borderRadius: 10, minHeight: 48, width: "100%",
            }}
          >
            {t("page_my_places_save_cta")}
          </Link>
        </div>
      )}

      {loading ? (
        <div className="px-5 mt-10 text-center">
          <Loader2 className="w-5 h-5 animate-spin mx-auto text-foreground/40" />
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={MapPin}
          message={t("page_my_places_empty")}
          testid="my-places-empty"
        />
      ) : (
        <ul className="px-5 mt-6 space-y-2.5">
          {items.map((c) => (
            <li key={c.id} className="lux-card p-3" data-testid={`my-places-${c.id}`}>
              <div className="flex items-center gap-3">
                <Thumb url={c.photos?.[0]?.url} icon={Gem} />
                <div className="flex-1 min-w-0">
                  <Link to={`/my-places/custom/${c.id}`} className="font-display text-base text-foreground truncate block">{c.name}</Link>
                  {c.description ? (
                    <div className="text-[11px] text-foreground/55 mt-0.5 line-clamp-2"
                      style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                      {c.description}
                    </div>
                  ) : null}
                  {c.address ? (
                    <div className="text-[11px] text-foreground/45 mt-0.5 truncate">{c.address}</div>
                  ) : null}
                </div>
              </div>
              {c.lat != null && c.lng != null && (
                <CompactActionsRow
                  destination={{
                    id: c.id,
                    name: c.name,
                    address: c.address,
                    lat: c.lat,
                    lng: c.lng,
                    category: c.category,
                    neighborhood: c.neighborhood,
                    phone: c.phone,
                    whatsapp: c.whatsapp,
                  }}
                  testidPrefix={`my-places-${c.id}`}
                />
              )}
            </li>
          ))}
        </ul>
      )}

      <BottomNav />
    </main>
  );
}
